import { Heading, Link, Paragraph, Stack, Text, useModals } from '@customerio/pluma-components/react';
import { formatCentsToUsd } from '@/utils/currency';
import { type Plan, PlanType } from '@/services/billing/plans';
import type { BillingPlan } from '@/services/billing/types';
import { PlanIncludedModal } from './plan-included-modal';
import * as styles from './plan-info.css';

/** The pinned-plan checkout price shown as "Base cost {amount} per {interval}" instead of "Starts at". */
export type CheckoutPinnedPrice = {
  /** The pinned plan's base cost (+ CDP premium), in cents. */
  amountCents: number;
  /** The billing interval, e.g. "month". */
  interval: string;
  /** The pinned plan itself; its real allowances fill the "What's included in my plan?" modal. */
  plan?: BillingPlan;
};

type PlanNameHeaderProps = {
  plan: Plan;
  /**
   * Whether the account's subscription starts in the future. Nothing is charged today, so the price is
   * replaced by the plan description plus a link to the plan's allowances, and the description below is
   * dropped.
   */
  startDateInFuture: boolean;
  /** In checkout, the pinned plan's actual price to show instead of the static "Starts at" price. */
  checkoutPrice?: CheckoutPinnedPrice;
  /** When on, the allowances modal shows profiles instead of contacts and hides the objects block. */
  newPipelinesObjectsPricing?: boolean;
};

// Plans that show a concrete "Starts at $X per month" price; every other plan shows "Custom Pricing".
const PRICED_PLAN_TYPES: PlanType[] = [PlanType.Essentials, PlanType.Premium, PlanType.CdpStarter];

type PinnedFutureStartProps = {
  description: string | undefined;
  pinnedPlan: BillingPlan | undefined;
  newPipelinesObjectsPricing: boolean;
};

/**
 * The pinned-plan header for a subscription that starts in the future: no price (nothing is charged
 * today), just the plan description and a link to what the plan includes.
 */
function PinnedFutureStart({ description, pinnedPlan, newPipelinesObjectsPricing }: PinnedFutureStartProps) {
  const modals = useModals();

  return (
    <>
      <Text color="primary" display="block">
        {description}
      </Text>
      {pinnedPlan == null ? null : (
        <Link
          data-test-included-in-plan-modal
          variant="primary"
          onClick={() => modals.open(PlanIncludedModal, { plan: pinnedPlan, newPipelinesObjectsPricing })}
        >
          What's included in my plan?
        </Link>
      )}
    </>
  );
}

/**
 * The header of a plan card: the plan name, its price, and its description. Shows the pinned plan's
 * actual "Base cost … per {interval}" during checkout (when `checkoutPrice` is supplied), a concrete
 * "Starts at $X per month" for priced plans, otherwise "Custom Pricing". A pinned plan whose start date
 * is in the future shows the plan description and an allowances link instead of a price.
 */
export function PlanNameHeader({
  plan,
  startDateInFuture,
  checkoutPrice,
  newPipelinesObjectsPricing = false,
}: PlanNameHeaderProps) {
  const showPrice = PRICED_PLAN_TYPES.includes(plan.planType);
  const formattedPrice = formatCentsToUsd(plan.basePrice, 0);

  return (
    <div data-test-plan-header={plan.name}>
      <Heading level="3" textAlign="center" size="xs" weight="bold" textTransform="uppercase" data-test-plan-name>
        {plan.name}
      </Heading>
      <Stack gap="150" mt="150" textAlign="center">
        {checkoutPrice != null ? (
          startDateInFuture ? (
            <PinnedFutureStart
              description={plan.description}
              pinnedPlan={checkoutPrice.plan}
              newPipelinesObjectsPricing={newPipelinesObjectsPricing}
            />
          ) : (
            <>
              <Text color="primary" size="xxs" display="block">
                Base cost
              </Text>
              <Text className={styles.priceAmount} color="primary" size="xxxl" weight="bold" display="block">
                {formatCentsToUsd(checkoutPrice.amountCents, 0)}
              </Text>
              <Text color="primary" size="xxs" display="block">
                per {checkoutPrice.interval}
              </Text>
            </>
          )
        ) : showPrice ? (
          <>
            <Text color="primary" size="xxs" display="block">
              Starts at
            </Text>
            <Text className={styles.priceAmount} color="primary" size="xxxl" weight="bold" display="block">
              {formattedPrice}
            </Text>
            <Text color="primary" size="xxs" display="block">
              per month
            </Text>
          </>
        ) : (
          <Text className={styles.customPricingText} color="primary" size="xxl" weight="bold" py="400">
            Custom Pricing
          </Text>
        )}
      </Stack>
      {startDateInFuture ? null : (
        <Paragraph text="product-pxs" mt="200" mx="300" textAlign="center" color="primary">
          {plan.description}
        </Paragraph>
      )}
    </div>
  );
}
